import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { MenuItem, SelectField } from '@curves/ui-base';
import { findCategoryNodeById } from '@Local/Product/Form/productFormValidation';

/**
 * 多層連動分類選單
 * - 下拉個數依資料層數決定，不設上限；選到有子節點的節點才出現下一層
 * - 改選上層時清空所有下層（由 `onChange` 回傳的新路徑自然不含被清空的層級）
 *
 * @param {object} props
 * @param {Array} props.categories 分類樹
 * @param {Array<number|string>} props.value 已選路徑（saleCategoryID，由第一層到目前選到的層）
 * @param {(next: Array<number|string>) => void} props.onChange
 * @param {boolean} [props.isError]
 * @param {string} [props.helperText]
 */
const CategoryCascadeSelect = ({ categories = [], value = [], onChange, isError = false, helperText = '' }) => {
    // 依已選路徑逐層展開，遇到未選或葉節點即停，層數由資料決定
    const levelNodesList = useMemo(() => {
        const levels = [];
        let currentNodes = categories;
        let level = 0;
        while (currentNodes && currentNodes.length > 0) {
            levels.push(currentNodes);
            const selectedNode = findCategoryNodeById(currentNodes, value[level]);
            if (!selectedNode || !selectedNode.children || selectedNode.children.length === 0) break;
            currentNodes = selectedNode.children;
            level += 1;
        }
        return levels;
    }, [categories, value]);

    // 改選某層時保留上層、清掉下層，選回「請選擇」則只截斷
    const handleLevelChange = (level, nextID) => {
        const nextValue = value.slice(0, level);
        if (nextID !== '') nextValue.push(nextID);
        onChange(nextValue);
    };

    return (
        // 不用 Grid container，避免負邊距造成對不齊
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', columnGap: 2, alignItems: 'start' }}>
            {levelNodesList.map((nodes, level) => (
                <Box key={level}>
                    <SelectField
                        label={level === 0 ? '商品分類' : undefined}
                        reserveLabelSpace
                        value={value[level] !== undefined ? String(value[level]) : ''}
                        onChange={(_e, _node, nextValue) => handleLevelChange(level, nextValue)}
                        displayEmpty
                        fullWidth
                        required={level === 0}
                        isError={level === 0 ? isError : false}
                        helperText={level === 0 ? helperText : undefined}
                    >
                        <MenuItem disabled value="">
                            <em>請選擇</em>
                        </MenuItem>
                        {nodes.map((node) => (
                            <MenuItem key={node.categoryID} value={String(node.categoryID)}>
                                {node.text}
                            </MenuItem>
                        ))}
                    </SelectField>
                </Box>
            ))}
        </Box>
    );
};

export default CategoryCascadeSelect;
